import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import {
  Circle,
  Calculator,
  Info,
  BookOpen,
  Lightbulb,
  Copy,
  Check,
  RotateCcw,
  Disc,
  PieChart,
  Ruler,
  TrendingUp,
  Zap,
  Eye,
  EyeOff,
  Download,
  Share2,
  History,
  Sparkles
} from 'lucide-react';
import "@/App.css";

const PI = Math.PI;

// Advanced Circle Diagram Component with multiple visualization modes
const CircleDiagram = ({ radius, centralAngle, highlightSector, showLabels = true, showGrid = true, mode = 'full' }) => {
  const centerX = 150;
  const centerY = 150;
  const scale = Math.min(100, 100 / Math.max(1, radius / 5));
  const scaledRadius = radius * scale;
  
  const angleRad = (centralAngle * PI) / 180;
  const endX = centerX + scaledRadius * Math.cos(-angleRad + PI/2);
  const endY = centerY - scaledRadius * Math.sin(-angleRad + PI/2);
  
  const largeArcFlag = centralAngle > 180 ? 1 : 0;
  
  return (
    <svg viewBox="0 0 300 300" className="w-full h-full max-w-[360px] mx-auto">
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <linearGradient id="circleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(190, 95%, 39%)" stopOpacity="0.2" />
          <stop offset="100%" stopColor="hsl(199, 89%, 48%)" stopOpacity="0.05" />
        </linearGradient>
        <pattern id="gridPattern" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="hsl(var(--border))" strokeWidth="0.5" opacity="0.3"/>
        </pattern>
      </defs>
      
      {/* Grid background */}
      {showGrid && (
        <>
          <rect width="300" height="300" fill="url(#gridPattern)" />
          <line x1="0" y1="150" x2="300" y2="150" stroke="hsl(var(--border))" strokeWidth="1.5" />
          <line x1="150" y1="0" x2="150" y2="300" stroke="hsl(var(--border))" strokeWidth="1.5" />
        </>
      )}
      
      {/* Circle */}
      <circle
        cx={centerX}
        cy={centerY}
        r={scaledRadius}
        fill="url(#circleGradient)"
        stroke="hsl(190, 95%, 39%)"
        strokeWidth="3"
        filter="url(#glow)"
      />
      
      {/* Sector (if highlighted) */}
      {highlightSector && centralAngle > 0 && centralAngle < 360 && (
        <>
          <path
            d={`M ${centerX} ${centerY} L ${centerX} ${centerY - scaledRadius} A ${scaledRadius} ${scaledRadius} 0 ${largeArcFlag} 1 ${endX} ${endY} Z`}
            fill="#10b981"
            fillOpacity="0.25"
            stroke="#10b981"
            strokeWidth="2.5"
          />
          <line x1={centerX} y1={centerY} x2={centerX} y2={centerY - scaledRadius} stroke="#10b981" strokeWidth="2.5" />
          <line x1={centerX} y1={centerY} x2={endX} y2={endY} stroke="#10b981" strokeWidth="2.5" />
          
          {/* Arc highlight */}
          <path
            d={`M ${centerX} ${centerY - scaledRadius} A ${scaledRadius} ${scaledRadius} 0 ${largeArcFlag} 1 ${endX} ${endY}`}
            fill="none"
            stroke="#22c55e"
            strokeWidth="3.5"
            strokeDasharray="5 3"
          />
        </>
      )}
      
      {/* Radius lines */}
      <line
        x1={centerX}
        y1={centerY}
        x2={centerX + scaledRadius}
        y2={centerY}
        stroke="#ef4444"
        strokeWidth="2.5"
      />
      
      {/* Diameter line */}
      <line
        x1={centerX - scaledRadius}
        y1={centerY}
        x2={centerX + scaledRadius}
        y2={centerY}
        stroke="#3b82f6"
        strokeWidth="2"
        strokeDasharray="6 4"
      />
      
      {/* Center point */}
      <circle cx={centerX} cy={centerY} r="5" fill="hsl(var(--foreground))" stroke="white" strokeWidth="2" />
      
      {/* Labels */}
      {showLabels && (
        <>
          <text
            x={centerX + scaledRadius/2}
            y={centerY - 12}
            fontSize="13"
            fill="#ef4444"
            fontWeight="700"
            textAnchor="middle"
          >
            r={radius.toFixed(2)}
          </text>
          
          <text
            x={centerX}
            y={centerY + 20}
            fontSize="12"
            fill="#3b82f6"
            fontWeight="600"
            textAnchor="middle"
          >
            d={((radius * 2).toFixed(2))}
          </text>
        </>
      )}
      
      {/* Angle arc for sector */}
      {highlightSector && centralAngle > 0 && centralAngle < 360 && (
        <>
          <path
            d={`M ${centerX + 30} ${centerY} A 30 30 0 ${largeArcFlag} 1 ${centerX + 30*Math.cos(-angleRad + PI/2)} ${centerY - 30*Math.sin(-angleRad + PI/2)}`}
            fill="none"
            stroke="#10b981"
            strokeWidth="2.5"
          />
          <text
            x={centerX + 42}
            y={centerY - 22}
            fontSize="13"
            fill="#10b981"
            fontWeight="700"
          >
            {centralAngle}°
          </text>
        </>
      )}
      
      {/* Vertex points */}
      {highlightSector && centralAngle > 0 && (
        <>
          <circle cx={centerX} cy={centerY - scaledRadius} r="4" fill="#22c55e" stroke="white" strokeWidth="2" />
          <circle cx={endX} cy={endY} r="4" fill="#22c55e" stroke="white" strokeWidth="2" />
        </>
      )}
    </svg>
  );
};

// Copy Button Component with animation
const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button 
      variant="ghost" 
      size="icon" 
      className="h-7 w-7 hover:bg-primary/10 transition-colors" 
      onClick={handleCopy}
      title={`Copy ${label || 'value'}`}
    >
      <AnimatePresence mode="wait">
        {copied ? (
          <motion.div
            key="check"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
          >
            <Check className="w-3.5 h-3.5 text-green-500" />
          </motion.div>
        ) : (
          <motion.div
            key="copy"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
          >
            <Copy className="w-3.5 h-3.5" />
          </motion.div>
        )}
      </AnimatePresence>
    </Button>
  );
};

// Property Card Component
const PropertyCard = ({ label, value, color, icon: Icon, formula }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    className={`rounded-xl p-4 ${color} border`}
  >
    <div className="flex items-start justify-between mb-2">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
      </div>
      <CopyButton text={value} label={label} />
    </div>
    <div className="font-mono text-lg font-bold text-foreground mb-1">
      {value}
    </div>
    {formula && (
      <div className="text-xs text-muted-foreground font-medium mt-2 font-mono bg-background/50 px-2 py-1 rounded">
        {formula}
      </div>
    )}
  </motion.div>
);

function CircleCalculator() {
  const [radius, setRadius] = useState(5);
  const [centralAngle, setCentralAngle] = useState(90);
  const [inputMode, setInputMode] = useState('radius');
  const [history, setHistory] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showFormulas, setShowFormulas] = useState(true);
  
  // Calculate all circle properties with high precision
  const properties = useMemo(() => {
    const r = radius;
    const diameter = 2 * r;
    const circumference = 2 * PI * r;
    const area = PI * r * r;
    
    const angleRad = (centralAngle * PI) / 180;
    const arcLength = r * angleRad;
    const sectorArea = 0.5 * r * r * angleRad;
    const chordLength = 2 * r * Math.sin(angleRad / 2);
    const segmentArea = sectorArea - (0.5 * r * r * Math.sin(angleRad));
    const segmentHeight = r * (1 - Math.cos(angleRad / 2));
    
    // Additional advanced properties
    const inscribedSquareSide = r * Math.sqrt(2);
    const inscribedSquareArea = 2 * r * r;
    const circumscribedSquareSide = 2 * r;
    const circumscribedSquareArea = 4 * r * r;
    const inscribedTriangleSide = r * Math.sqrt(3);
    const inscribedTriangleArea = (3 * Math.sqrt(3) * r * r) / 4;
    
    return {
      radius: r,
      diameter,
      circumference,
      area,
      arcLength,
      sectorArea,
      chordLength,
      segmentArea,
      segmentHeight,
      centralAngle,
      angleRad,
      inscribedSquareSide,
      inscribedSquareArea,
      circumscribedSquareSide,
      circumscribedSquareArea,
      inscribedTriangleSide,
      inscribedTriangleArea
    };
  }, [radius, centralAngle]);
  
  // Input handlers
  const handleRadiusChange = useCallback((value) => {
    const newRadius = Math.max(0.1, parseFloat(value) || 0);
    setRadius(newRadius);
    addToHistory('radius', newRadius);
  }, []);
  
  const handleDiameterChange = useCallback((value) => {
    const d = Math.max(0.1, parseFloat(value) || 0);
    setRadius(d / 2);
    addToHistory('diameter', d);
  }, []);
  
  const handleCircumferenceChange = useCallback((value) => {
    const c = Math.max(0.1, parseFloat(value) || 0);
    setRadius(c / (2 * PI));
    addToHistory('circumference', c);
  }, []);
  
  const handleAreaChange = useCallback((value) => {
    const a = Math.max(0.1, parseFloat(value) || 0);
    setRadius(Math.sqrt(a / PI));
    addToHistory('area', a);
  }, []);
  
  const addToHistory = (type, value) => {
    const timestamp = new Date().toLocaleTimeString();
    setHistory(prev => [{ type, value, timestamp }, ...prev].slice(0, 10));
  };
  
  const handleReset = useCallback(() => {
    setRadius(5);
    setCentralAngle(90);
    setInputMode('radius');
    setHistory([]);
  }, []);
  
  const handleExport = () => {
    const data = {
      ...properties,
      timestamp: new Date().toISOString(),
      inputMode
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `circle-calculation-${Date.now()}.json`;
    a.click();
  };
  
  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <Helmet>
        <title>Circle Properties Calculator - Area, Circumference, Arc, Sector</title>
        <meta name="description" content="Professional circle calculator with advanced features. Calculate area, circumference, arc length, sector area, chord length, segment properties, and inscribed/circumscribed shapes." />
      </Helmet>
      
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-lg">
              <Circle className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Circle Properties Calculator</h1>
              <p className="text-muted-foreground mt-1">Professional tool for all circle calculations</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExport} className="gap-2">
              <Download className="w-4 h-4" />
              Export
            </Button>
            <Button variant="outline" size="sm" className="gap-2">
              <Share2 className="w-4 h-4" />
              Share
            </Button>
          </div>
        </div>
        
        {/* Quick Stats Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-3"
        >
          <Card className="p-3 bg-cyan-500/5 border-cyan-500/20">
            <div className="text-xs text-muted-foreground mb-1">Area</div>
            <div className="text-lg font-bold text-cyan-600">{properties.area.toFixed(2)}</div>
          </Card>
          <Card className="p-3 bg-blue-500/5 border-blue-500/20">
            <div className="text-xs text-muted-foreground mb-1">Circumference</div>
            <div className="text-lg font-bold text-blue-600">{properties.circumference.toFixed(2)}</div>
          </Card>
          <Card className="p-3 bg-green-500/5 border-green-500/20">
            <div className="text-xs text-muted-foreground mb-1">Diameter</div>
            <div className="text-lg font-bold text-green-600">{properties.diameter.toFixed(2)}</div>
          </Card>
          <Card className="p-3 bg-purple-500/5 border-purple-500/20">
            <div className="text-xs text-muted-foreground mb-1">Radius</div>
            <div className="text-lg font-bold text-purple-600">{properties.radius.toFixed(2)}</div>
          </Card>
        </motion.div>
      </motion.div>
      
      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Input Panel */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:sticky lg:top-24 lg:self-start space-y-6"
        >
          {/* Input Controls */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-cyan-600" />
                <span className="font-semibold text-foreground text-lg">Input Values</span>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-9 w-9 hover:bg-destructive/10 hover:text-destructive" 
                onClick={handleReset}
                title="Reset All"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Input Mode Selection */}
            <div className="mb-6">
              <Label className="text-sm text-muted-foreground mb-3 block font-medium">Calculate From</Label>
              <Tabs value={inputMode} onValueChange={setInputMode} className="w-full">
                <TabsList className="w-full grid grid-cols-2 h-auto">
                  <TabsTrigger value="radius" className="text-xs py-2">
                    <Ruler className="w-3 h-3 mr-1.5" />
                    Radius
                  </TabsTrigger>
                  <TabsTrigger value="diameter" className="text-xs py-2">
                    <Ruler className="w-3 h-3 mr-1.5" />
                    Diameter
                  </TabsTrigger>
                </TabsList>
                <TabsList className="w-full grid grid-cols-2 mt-2 h-auto">
                  <TabsTrigger value="circumference" className="text-xs py-2">
                    <Circle className="w-3 h-3 mr-1.5" />
                    Circumference
                  </TabsTrigger>
                  <TabsTrigger value="area" className="text-xs py-2">
                    <PieChart className="w-3 h-3 mr-1.5" />
                    Area
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            
            {/* Input Fields */}
            <div className="space-y-5">
              {inputMode === 'radius' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Label className="text-sm mb-2 block font-medium">Radius (r)</Label>
                  <Input
                    type="number"
                    value={radius}
                    onChange={(e) => handleRadiusChange(e.target.value)}
                    className="h-12 text-lg font-mono"
                    min="0"
                    step="0.1"
                  />
                  <div className="text-xs text-muted-foreground mt-1.5">Enter the radius of the circle</div>
                </motion.div>
              )}
              
              {inputMode === 'diameter' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Label className="text-sm mb-2 block font-medium">Diameter (d)</Label>
                  <Input
                    type="number"
                    value={properties.diameter}
                    onChange={(e) => handleDiameterChange(e.target.value)}
                    className="h-12 text-lg font-mono"
                    min="0"
                    step="0.1"
                  />
                  <div className="text-xs text-muted-foreground mt-1.5">Enter the diameter of the circle</div>
                </motion.div>
              )}
              
              {inputMode === 'circumference' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Label className="text-sm mb-2 block font-medium">Circumference (C)</Label>
                  <Input
                    type="number"
                    value={properties.circumference}
                    onChange={(e) => handleCircumferenceChange(e.target.value)}
                    className="h-12 text-lg font-mono"
                    min="0"
                    step="0.1"
                  />
                  <div className="text-xs text-muted-foreground mt-1.5">Enter the circumference of the circle</div>
                </motion.div>
              )}
              
              {inputMode === 'area' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Label className="text-sm mb-2 block font-medium">Area (A)</Label>
                  <Input
                    type="number"
                    value={properties.area}
                    onChange={(e) => handleAreaChange(e.target.value)}
                    className="h-12 text-lg font-mono"
                    min="0"
                    step="0.1"
                  />
                  <div className="text-xs text-muted-foreground mt-1.5">Enter the area of the circle</div>
                </motion.div>
              )}
              
              {/* Central Angle for Arc/Sector */}
              <div className="pt-5 border-t border-border">
                <Label className="text-sm mb-2 font-medium flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Central Angle (for arc/sector calculations)
                </Label>
                <Input
                  type="number"
                  value={centralAngle}
                  onChange={(e) => setCentralAngle(Math.max(0, Math.min(360, parseFloat(e.target.value) || 0)))}
                  className="h-12 text-lg font-mono"
                  min="0"
                  max="360"
                  step="1"
                />
                <input
                  type="range"
                  min="0"
                  max="360"
                  step="1"
                  value={centralAngle}
                  onChange={(e) => setCentralAngle(parseFloat(e.target.value))}
                  className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-cyan-600 mt-4"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1.5">
                  <span>0°</span>
                  <span className="font-semibold text-cyan-600">{centralAngle}°</span>
                  <span>360°</span>
                </div>
              </div>
            </div>
            
            {/* Quick Angle Presets */}
            <div className="mt-6">
              <Label className="text-xs text-muted-foreground mb-3 block font-medium">Quick Angle Presets</Label>
              <div className="flex flex-wrap gap-2">
                {[30, 45, 60, 90, 120, 135, 150, 180, 270, 360].map((angle) => (
                  <Button
                    key={angle}
                    variant="outline"
                    size="sm"
                    className={`text-xs rounded-lg transition-all ${
                      centralAngle === angle 
                        ? 'bg-cyan-600 text-white border-cyan-600 hover:bg-cyan-700 hover:text-white' 
                        : 'hover:bg-cyan-50 hover:border-cyan-300'
                    }`}
                    onClick={() => setCentralAngle(angle)}
                  >
                    {angle}°
                  </Button>
                ))}
              </div>
            </div>
          </Card>
          
          {/* Calculation History */}
          {history.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="rounded-2xl p-5 border shadow-md">
                <div className="flex items-center gap-2 mb-4">
                  <History className="w-4 h-4 text-muted-foreground" />
                  <span className="font-semibold text-sm">Recent Calculations</span>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {history.map((item, index) => (
                    <div key={index} className="text-xs p-2 rounded bg-muted/50 flex justify-between items-center">
                      <span className="font-medium capitalize">{item.type}: {item.value.toFixed(2)}</span>
                      <span className="text-muted-foreground">{item.timestamp}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>
          )}
        </motion.div>
        
        {/* Center/Right Column - Visualization & Results */}
        <div className="lg:col-span-2 space-y-6">
          {/* Interactive Visualization */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="rounded-2xl p-6 border-2 shadow-lg">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-semibold text-foreground flex items-center gap-2 text-lg">
                  <Disc className="w-5 h-5 text-cyan-600" />
                  Interactive Circle Diagram
                  <Badge variant="secondary" className="ml-2 text-xs">Real-time</Badge>
                </h2>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFormulas(!showFormulas)}
                    className="gap-2"
                  >
                    {showFormulas ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    <span className="hidden sm:inline">Formulas</span>
                  </Button>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                    <Circle className="w-4 h-4" />
                    Full Circle View
                  </h3>
                  <div className="rounded-xl p-5 bg-gradient-to-br from-muted/30 to-muted/10 border-2 border-border">
                    <CircleDiagram radius={radius} centralAngle={0} highlightSector={false} />
                  </div>
                  <div className="mt-3 text-xs text-center text-muted-foreground">
                    Complete circle with radius {radius.toFixed(2)}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                    <PieChart className="w-4 h-4" />
                    Sector & Arc View ({centralAngle}°)
                  </h3>
                  <div className="rounded-xl p-5 bg-gradient-to-br from-green-500/5 to-emerald-500/5 border-2 border-green-500/20">
                    <CircleDiagram radius={radius} centralAngle={centralAngle} highlightSector={true} />
                  </div>
                  <div className="mt-3 text-xs text-center text-muted-foreground">
                    Sector with {centralAngle}° central angle
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
          
          {/* All Properties - Basic */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <h2 className="font-semibold text-foreground flex items-center gap-2 mb-5 text-lg">
              <Info className="w-5 h-5 text-cyan-600" />
              Basic Circle Properties
              <Sparkles className="w-4 h-4 text-yellow-500 ml-auto" />
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <PropertyCard
                label="Radius"
                value={properties.radius.toFixed(6)}
                color="bg-red-500/10 border-red-500/20"
                icon={Ruler}
                formula={showFormulas ? "r (given)" : null}
              />
              <PropertyCard
                label="Diameter"
                value={properties.diameter.toFixed(6)}
                color="bg-blue-500/10 border-blue-500/20"
                icon={Ruler}
                formula={showFormulas ? "d = 2r" : null}
              />
              <PropertyCard
                label="Circumference"
                value={properties.circumference.toFixed(6)}
                color="bg-cyan-500/10 border-cyan-500/20"
                icon={Circle}
                formula={showFormulas ? "C = 2πr" : null}
              />
              <PropertyCard
                label="Area"
                value={properties.area.toFixed(6)}
                color="bg-purple-500/10 border-purple-500/20"
                icon={PieChart}
                formula={showFormulas ? "A = πr²" : null}
              />
            </div>
          </Card>
          
          {/* Arc & Sector Properties */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-green-500/5 to-emerald-500/5">
            <h2 className="font-semibold text-foreground flex items-center gap-2 mb-5 text-lg">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Arc & Sector Properties ({centralAngle}°)
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <PropertyCard
                label="Arc Length"
                value={properties.arcLength.toFixed(6)}
                color="bg-green-500/10 border-green-500/20"
                formula={showFormulas ? "L = rθ (θ in radians)" : null}
              />
              <PropertyCard
                label="Sector Area"
                value={properties.sectorArea.toFixed(6)}
                color="bg-emerald-500/10 border-emerald-500/20"
                formula={showFormulas ? "A = ½r²θ" : null}
              />
              <PropertyCard
                label="Chord Length"
                value={properties.chordLength.toFixed(6)}
                color="bg-teal-500/10 border-teal-500/20"
                formula={showFormulas ? "c = 2r·sin(θ/2)" : null}
              />
              <PropertyCard
                label="Angle (radians)"
                value={properties.angleRad.toFixed(6)}
                color="bg-amber-500/10 border-amber-500/20"
                formula={showFormulas ? "θ = angle × π/180" : null}
              />
              <PropertyCard
                label="Segment Area"
                value={properties.segmentArea.toFixed(6)}
                color="bg-lime-500/10 border-lime-500/20"
                formula={showFormulas ? "A = sector - triangle" : null}
              />
              <PropertyCard
                label="Segment Height"
                value={properties.segmentHeight.toFixed(6)}
                color="bg-green-600/10 border-green-600/20"
                formula={showFormulas ? "h = r(1 - cos(θ/2))" : null}
              />
            </div>
          </Card>
          
          {/* Advanced Properties - Toggle */}
          <div>
            <Button
              variant="outline"
              className="w-full mb-4 gap-2"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showAdvanced ? 'Hide' : 'Show'} Advanced Properties
              <Zap className="w-4 h-4 ml-auto text-yellow-500" />
            </Button>
            
            <AnimatePresence>
              {showAdvanced && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-purple-500/5 to-pink-500/5">
                    <h2 className="font-semibold text-foreground flex items-center gap-2 mb-5 text-lg">
                      <Sparkles className="w-5 h-5 text-purple-600" />
                      Inscribed & Circumscribed Shapes
                    </h2>
                    
                    <div className="space-y-6">
                      {/* Inscribed Shapes */}
                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                          Inscribed Shapes (Inside Circle)
                        </h3>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <PropertyCard
                            label="Inscribed Square - Side"
                            value={properties.inscribedSquareSide.toFixed(6)}
                            color="bg-purple-500/10 border-purple-500/20"
                            formula={showFormulas ? "s = r√2" : null}
                          />
                          <PropertyCard
                            label="Inscribed Square - Area"
                            value={properties.inscribedSquareArea.toFixed(6)}
                            color="bg-purple-500/10 border-purple-500/20"
                            formula={showFormulas ? "A = 2r²" : null}
                          />
                          <PropertyCard
                            label="Inscribed Triangle - Side"
                            value={properties.inscribedTriangleSide.toFixed(6)}
                            color="bg-pink-500/10 border-pink-500/20"
                            formula={showFormulas ? "s = r√3" : null}
                          />
                          <PropertyCard
                            label="Inscribed Triangle - Area"
                            value={properties.inscribedTriangleArea.toFixed(6)}
                            color="bg-pink-500/10 border-pink-500/20"
                            formula={showFormulas ? "A = (3√3/4)r²" : null}
                          />
                        </div>
                      </div>
                      
                      {/* Circumscribed Shapes */}
                      <div className="pt-4 border-t border-border">
                        <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                          Circumscribed Shapes (Outside Circle)
                        </h3>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <PropertyCard
                            label="Circumscribed Square - Side"
                            value={properties.circumscribedSquareSide.toFixed(6)}
                            color="bg-indigo-500/10 border-indigo-500/20"
                            formula={showFormulas ? "s = 2r" : null}
                          />
                          <PropertyCard
                            label="Circumscribed Square - Area"
                            value={properties.circumscribedSquareArea.toFixed(6)}
                            color="bg-indigo-500/10 border-indigo-500/20"
                            formula={showFormulas ? "A = 4r²" : null}
                          />
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Comprehensive Formulas Guide */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-cyan-600" />
              Complete Circle Formulas Reference
            </h2>
            
            <div className="space-y-5">
              {/* Basic Formulas */}
              <div className="p-5 rounded-xl bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20">
                <h3 className="font-bold text-foreground mb-3 flex items-center gap-2">
                  <Circle className="w-4 h-4 text-cyan-600" />
                  Basic Circle Formulas
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-background/60 rounded-lg">
                    <span className="text-muted-foreground font-medium min-w-[140px]">Circumference:</span>
                    <code className="bg-muted px-3 py-1.5 rounded font-mono text-xs flex-1">C = 2πr = πd</code>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-background/60 rounded-lg">
                    <span className="text-muted-foreground font-medium min-w-[140px]">Area:</span>
                    <code className="bg-muted px-3 py-1.5 rounded font-mono text-xs flex-1">A = πr²</code>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-background/60 rounded-lg">
                    <span className="text-muted-foreground font-medium min-w-[140px]">Diameter:</span>
                    <code className="bg-muted px-3 py-1.5 rounded font-mono text-xs flex-1">d = 2r</code>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-background/60 rounded-lg">
                    <span className="text-muted-foreground font-medium min-w-[140px]">Radius:</span>
                    <code className="bg-muted px-3 py-1.5 rounded font-mono text-xs flex-1">r = d/2 = C/(2π) = √(A/π)</code>
                  </div>
                </div>
              </div>
              
              {/* Arc & Sector Formulas */}
              <div className="p-5 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20">
                <h3 className="font-bold text-green-600 mb-3 flex items-center gap-2">
                  <PieChart className="w-4 h-4" />
                  Arc & Sector Formulas
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-background/60 rounded-lg">
                    <span className="text-muted-foreground font-medium min-w-[140px]">Arc Length:</span>
                    <code className="bg-muted px-3 py-1.5 rounded font-mono text-xs flex-1">L = rθ (θ in radians) = (πrθ)/180° (θ in degrees)</code>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-background/60 rounded-lg">
                    <span className="text-muted-foreground font-medium min-w-[140px]">Sector Area:</span>
                    <code className="bg-muted px-3 py-1.5 rounded font-mono text-xs flex-1">A = ½r²θ (θ in radians) = (πr²θ)/360° (θ in degrees)</code>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-background/60 rounded-lg">
                    <span className="text-muted-foreground font-medium min-w-[140px]">Chord Length:</span>
                    <code className="bg-muted px-3 py-1.5 rounded font-mono text-xs flex-1">c = 2r·sin(θ/2)</code>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-background/60 rounded-lg">
                    <span className="text-muted-foreground font-medium min-w-[140px]">Segment Area:</span>
                    <code className="bg-muted px-3 py-1.5 rounded font-mono text-xs flex-1">A = ½r²(θ - sinθ)</code>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-background/60 rounded-lg">
                    <span className="text-muted-foreground font-medium min-w-[140px]">Segment Height:</span>
                    <code className="bg-muted px-3 py-1.5 rounded font-mono text-xs flex-1">h = r - r·cos(θ/2) = r(1 - cos(θ/2))</code>
                  </div>
                </div>
              </div>
              
              {/* Advanced Formulas */}
              <div className="p-5 rounded-xl bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20">
                <h3 className="font-bold text-purple-600 mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Inscribed & Circumscribed Shapes
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-background/60 rounded-lg">
                    <span className="text-muted-foreground font-medium min-w-[180px]">Inscribed Square Side:</span>
                    <code className="bg-muted px-3 py-1.5 rounded font-mono text-xs flex-1">s = r√2</code>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-background/60 rounded-lg">
                    <span className="text-muted-foreground font-medium min-w-[180px]">Inscribed Triangle Side:</span>
                    <code className="bg-muted px-3 py-1.5 rounded font-mono text-xs flex-1">s = r√3 (equilateral)</code>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-background/60 rounded-lg">
                    <span className="text-muted-foreground font-medium min-w-[180px]">Circumscribed Square:</span>
                    <code className="bg-muted px-3 py-1.5 rounded font-mono text-xs flex-1">s = 2r</code>
                  </div>
                </div>
              </div>
              
              {/* Conversions */}
              <div className="p-5 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20">
                <h3 className="font-bold text-amber-600 mb-3 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Useful Conversions & Constants
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-background/60 rounded-lg">
                    <span className="text-muted-foreground font-medium min-w-[180px]">π (Pi):</span>
                    <code className="bg-muted px-3 py-1.5 rounded font-mono text-xs flex-1">≈ 3.14159265359</code>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-background/60 rounded-lg">
                    <span className="text-muted-foreground font-medium min-w-[180px]">Degrees to Radians:</span>
                    <code className="bg-muted px-3 py-1.5 rounded font-mono text-xs flex-1">radians = degrees × π/180</code>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-background/60 rounded-lg">
                    <span className="text-muted-foreground font-medium min-w-[180px]">Radians to Degrees:</span>
                    <code className="bg-muted px-3 py-1.5 rounded font-mono text-xs flex-1">degrees = radians × 180/π</code>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          
          {/* Comprehensive FAQ */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-500" />
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="faq-1" className="border-b border-border">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium text-foreground">What is the difference between circumference and perimeter?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-muted-foreground leading-relaxed">
                    Circumference is a special term used specifically for the perimeter of a circle. While \"perimeter\" is a general term for the distance around any closed shape, \"circumference\" specifically refers to circles. They mean the same thing when talking about circles - the total distance around the edge. The formula for circumference is C = 2πr or C = πd.
                  </p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-2" className="border-b border-border">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium text-foreground">How do I calculate the area of a sector?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    A sector is like a \"pizza slice\" of the circle. There are two formulas depending on whether your angle is in degrees or radians:
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-600 font-bold mt-0.5">•</span>
                      <span><strong>In degrees:</strong> Area = (θ/360°) × πr²</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-600 font-bold mt-0.5">•</span>
                      <span><strong>In radians:</strong> Area = ½r²θ</span>
                    </li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mt-3">
                    For example, a 90° sector (quarter circle) with radius 5 has area = (90/360) × π × 5² ≈ 19.63 square units.
                  </p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-3" className="border-b border-border">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium text-foreground">What is a chord and how is it different from diameter?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    A chord is any straight line connecting two points on the circle's circumference. The diameter is actually a special type of chord - it's the longest possible chord that passes through the center of the circle.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-600 font-bold mt-0.5">•</span>
                      <span><strong>Chord:</strong> Any line segment with both endpoints on the circle</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-600 font-bold mt-0.5">•</span>
                      <span><strong>Diameter:</strong> A chord that passes through the center (length = 2r)</span>
                    </li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mt-3">
                    The formula for chord length when you know the central angle θ is: c = 2r·sin(θ/2)
                  </p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-4" className="border-b border-border">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium text-foreground">What is a circle segment and how is it calculated?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    A circle segment (or circular segment) is the region between a chord and the arc it cuts off. Think of it as a sector with the triangular part removed.
                  </p>
                  <div className="bg-muted/30 p-4 rounded-lg mb-3">
                    <p className="text-sm font-mono">Segment Area = Sector Area - Triangle Area</p>
                    <p className="text-sm font-mono mt-2">A = ½r²(θ - sin θ)</p>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">
                    The segment height (h) is the perpendicular distance from the chord to the arc, calculated as: h = r(1 - cos(θ/2))
                  </p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-5" className="border-b border-border">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium text-foreground">Why do we use radians instead of degrees?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    Radians are often preferred in mathematics and physics because they create simpler, more elegant formulas. One radian is the angle created when the arc length equals the radius.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-600 font-bold mt-0.5">•</span>
                      <span>Full circle = 360° = 2π radians</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-600 font-bold mt-0.5">•</span>
                      <span>Half circle = 180° = π radians</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-600 font-bold mt-0.5">•</span>
                      <span>Quarter circle = 90° = π/2 radians</span>
                    </li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mt-3">
                    Converting: radians = degrees × (π/180), degrees = radians × (180/π)
                  </p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-6" className="border-b border-border">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium text-foreground">What are inscribed and circumscribed shapes?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    These terms describe the relationship between a circle and another shape:
                  </p>
                  <div className="space-y-4">
                    <div className="bg-purple-500/10 p-4 rounded-lg border border-purple-500/20">
                      <h4 className="font-semibold text-sm mb-2">Inscribed (Inside the circle)</h4>
                      <p className="text-sm text-muted-foreground">
                        A shape is inscribed in a circle when all its vertices (corners) touch the circle. The circle is called the circumscribed circle. For example, an inscribed square in a circle with radius r has side length r√2.
                      </p>
                    </div>
                    <div className="bg-indigo-500/10 p-4 rounded-lg border border-indigo-500/20">
                      <h4 className="font-semibold text-sm mb-2">Circumscribed (Outside the circle)</h4>
                      <p className="text-sm text-muted-foreground">
                        A shape is circumscribed around a circle when all the circle's edges touch the shape's sides. The circle is called the inscribed circle. For example, a square circumscribed around a circle with radius r has side length 2r.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-7" className="border-0">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium text-foreground">How accurate is the value of π (pi) in these calculations?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    This calculator uses JavaScript's built-in Math.PI constant, which provides π accurate to about 15-17 decimal places:
                  </p>
                  <div className="bg-muted/30 p-4 rounded-lg mb-3">
                    <p className="text-sm font-mono">π ≈ 3.14159265358979323846...</p>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">
                    This level of precision is more than sufficient for virtually all practical applications, including engineering, architecture, and scientific calculations. For most everyday use, even 3.14 or 22/7 provides reasonable approximations, but our calculator uses the full precision available.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Card>
          
          {/* Real-world Applications */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-blue-500/5 to-cyan-500/5">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <Zap className="w-5 h-5 text-blue-600" />
              Real-World Applications
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <span className="text-2xl">🏗️</span>
                  Engineering & Construction
                </h3>
                <p className="text-xs text-muted-foreground">
                  Calculate pipe circumferences, circular foundation areas, dome structures, and wheel dimensions for vehicles and machinery.
                </p>
              </div>
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <span className="text-2xl">🌍</span>
                  Geography & Astronomy
                </h3>
                <p className="text-xs text-muted-foreground">
                  Measure Earth's circumference, calculate planetary orbits, determine satellite coverage areas, and map circular regions.
                </p>
              </div>
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <span className="text-2xl">🎨</span>
                  Art & Design
                </h3>
                <p className="text-xs text-muted-foreground">
                  Design circular logos, create pie charts and data visualizations, plan circular gardens, and calculate material needed for round objects.
                </p>
              </div>
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <span className="text-2xl">🍕</span>
                  Everyday Life
                </h3>
                <p className="text-xs text-muted-foreground">
                  Calculate pizza sizes, determine fence length for circular pools, measure plate and bowl capacities, and plan circular event seating.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default CircleCalculator;
